/**
 * When we use a color in the following format:
 *
 * color: {
 *   100: string;
 *   200: string;
 *   300: string;
 *   400: string;
 * }
 *
 * It represents the shades of that color, where:
 * - Lower numbers (e.g., 100) indicate darker shades.
 * - Higher numbers (e.g., 400) indicate lighter shades.
 *
 * This format ensures a consistent and clear way to define and use color shades.
 */

// Define the structure of a theme
export interface themeType {
  brand_blue: {
    1: string;
    2: string;
    3: string;
    4: string;
    5: string;
    6: string;
    7: string;
  };
  gray_blue: {
    1: string;
    2: string;
    3: string;
    4: string;
    5: string;
    6: string;
    7: string;
  };
  gray: {
    1: string;
    2: string;
    3: string;
    4: string;
    5: string;
    6: string;
    7: string;
    8: string;
  };
  green: {
    1: string;
    2: string;
    3: string;
    4: string;
    5: string;
  };
  teal: {
    1: string;
    2: string;
    3: string;
    4: string;
    5: string;
    6: string;
  };
  yellow: {
    1: string;
    2: string;
    3: string;
    4: string;
    5: string;
  };
  red: {
    1: string;
    2: string;
    3: string;
    4: string;
    5: string;
    6: string;
  };
  white: {
    1: string;
  };
  black: {
    1: string;
  };
  modalBackground: {
    1: string;
  };
  loadingBarBackground: {
    1: string;
  };
  toast: {
    1: string;
  };
  transparent: {
    1: string;
  };
}

interface themesType {
  light: themeType;
}

const themes: themesType = {
  light: {
    brand_blue: {
      1: '#2E3A4B',
      2: '#3F5678',
      3: '#005EE9',
      4: '#4D92F9',
      5: '#70AAFF',
      6: '#CCDFFB',
      7: '#E6EFFD',
    },
    gray_blue: {
      1: '#141A42',
      2: '#434868',
      3: '#72768E',
      4: '#A1A3B3',
      5: '#DADCDF',
      6: '#E6E8EC',
      7: '#F2F4F8',
    },
    gray: {
      1: '#121212',
      2: '#1E1E1E',
      3: '#2A2A2A',
      4: '#353535',
      5: '#606060',
      6: '#949494',
      7: '#C3C3C3',
      8: '#F3F3F3',
    },
    green: {
      1: '#1A3E28',
      2: '#146D36',
      3: '#0ABC4E',
      4: '#CEF2DC',
      5: '#E7F8ED',
    },
    teal: {
      1: '#2E3A4B',
      2: '#3F5678',
      3: '#005EE9',
      4: '#4D92F9',
      5: '#70AAFF',
      6: '#CCDFFB',
    },
    yellow: {
      1: '#2A2212',
      2: '#8E6A18',
      3: '#FEB612',
      4: '#FEDA88',
      5: '#FFF0D0',
    },
    red: {
      1: '#411F19',
      2: '#8E3928',
      3: '#E94E00',
      4: '#FF5533',
      5: '#F4A680',
      6: '#FDEDE6',
    },
    white: {
      1: '#FFFFFF',
    },
    black: {
      1: '#000000',
    },
    modalBackground: {
      1: 'rgba(0, 0, 0, 0.5)',
    },
    loadingBarBackground: {
      1: 'rgba(255, 255, 255, 0.8)',
    },
    toast: {
      1: '#E6F4F2',
    },
    transparent: {
      1: 'transparent',
    },
  },
};

const typeVariants = {
  h1: {
    fontFamily: 'SF Pro Display, sans-serif',
    fontWeight: 'bold',
    fontSize: 24,
    lineHeight: 30,
  },
  h2: {
    fontFamily: 'SF Pro Display, sans-serif',
    fontWeight: 'bold',
    fontSize: 20,
    lineHeight: 26,
  },
  h3: {
    fontFamily: 'SF Pro Display, sans-serif',
    fontWeight: '600', // Semibold corresponds to 600 in numeric.
    fontSize: 18,
    lineHeight: 24,
  },
  h4: {
    fontFamily: 'SF Pro Display, sans-serif',
    fontWeight: '600',
    fontSize: 16,
    lineHeight: 22,
  },
  title1: {
    fontFamily: 'SF Pro Display, sans-serif',
    fontWeight: '500', // Medium corresponds to 500 in numeric.
    fontSize: 16,
    lineHeight: 22,
  },
  title2: {
    fontFamily: 'SF Pro Display, sans-serif',
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 20,
  },
  title3: {
    fontFamily: 'SF Pro Display, sans-serif',
    fontWeight: '600',
    fontSize: 14,
    lineHeight: 20,
  },
  body1: {
    fontFamily: 'SF Pro Display, sans-serif',
    fontWeight: '400', // Regular corresponds to 400 in numeric.
    fontSize: 15,
    lineHeight: 20,
  },
  body2: {
    fontFamily: 'SF Pro Display, sans-serif',
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 20,
  },
  caption1Regular: {
    fontFamily: 'SF Pro Display, sans-serif',
    fontWeight: '400',
    fontSize: 12,
    lineHeight: 18,
  },
  caption1Medium: {
    fontFamily: 'SF Pro Display, sans-serif',
    fontWeight: '500',
    fontSize: 12,
    lineHeight: 18,
  },
  caption2: {
    fontFamily: 'SF Pro Display, sans-serif',
    fontWeight: '500',
    fontSize: 11,
    lineHeight: 16,
  },
};

export { themes, typeVariants };
